import LeftSummary from "./LeftSummary";
import RightActions from "./RightActions";
import UploadDocumentsCard from "./UploadDocumentsCard";
import AttachmentsListCard from "./AttachmentsListCard";
import MiddleSections from "./MiddleSections";
import CarrierActions from "./CarrierActions";
import { FundingRequestFormProvider, useFundingRequestForm } from "./FundingRequestFormContext";
import {
  FundingRequestUpdateActionProvider,
  useFundingRequestUpdateAction
} from "./FundingRequestUpdateActionContext";
import LoadingOverlay from "./LoadingOverlay";
import ReactDOM from "react-dom";
import { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import useDeviceType from "hooks/useDeviceType";
import useKeyboardHandling from "hooks/useKeyboardHandling";
import { FR_CONTRACT_TYPE, FR_STATUS } from "constants/haulpay-broker";
import Banner from "components/Banner";
import { useUserFeature } from "hooks/useUserFeature";
import Button from "components/Button";
import Icon from "@mdi/react";
import { mdiLoading, mdiFileDocumentEdit } from "@mdi/js";
import Tooltip from "components/Tooltip";
import clsx from "clsx";

function FundingRequestLayout() {
  const {
    isSubmitting,
    formMethods,
    hasBOLDocument,
    isAssignedLoadsPage,
    selectedFR,
    isFetchingCalculation,
    isLockedForReview,
    showCarrierSubmitActions
  } = useFundingRequestForm();
  const {
    isAssignedLoadsMode,
    isCompletionStateActive,
    isButtonDisabled,
    updateBtnLabel,
    handleSubmitClick
  } = useFundingRequestUpdateAction();
  const { isDesktop } = useDeviceType();
  const { isCarrier, isBroker, isNonFactorRefactorEnabled } = useUserFeature();
  const isKeyboardVisible = useKeyboardHandling();

  // Set browser tab title with load number
  useLayoutEffect(() => {
    const loadNumber = selectedFR?.userLoadNumber;
    if (loadNumber) {
      document.title = `L# ${loadNumber}`;
    } else {
      document.title = "Funding Request";
    }
    return () => {
      document.title = "HaulPay";
    };
  }, [selectedFR?.userLoadNumber]);

  const [swiperInstance, setSwiperInstance] = useState<any>(null);

  // Two anchors: one lives in the desktop rail, the other in the mobile flow
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Whichever anchor is currently visible becomes our active portal container
  const [activeAnchor, setActiveAnchor] = useState<HTMLDivElement | null>(null);

  // Ensure pagination is properly initialized
  useEffect(() => {
    if (swiperInstance && swiperInstance.pagination) {
      swiperInstance.pagination.init();
      swiperInstance.pagination.render();
      swiperInstance.pagination.update();
    }
  }, [swiperInstance]);

  // Helper to detect which anchor is visible (displayed by CSS, not "hidden")
  const pickVisibleAnchor = () => {
    const isVisible = (el: HTMLDivElement | null) => !!el && el.offsetParent !== null; // works with Tailwind's `hidden`
    if (isVisible(anchorRef.current)) return anchorRef.current;
    return null;
  };

  useEffect(() => {
    setActiveAnchor(pickVisibleAnchor());
    const handleResize = () => setActiveAnchor(pickVisibleAnchor());
    window.addEventListener("resize", handleResize);
    // Re-evaluate after layout shifts (e.g., sticky bars, async renders)
    const r = requestAnimationFrame(() => setActiveAnchor(pickVisibleAnchor()));

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(r);
    };
  }, []);

  const contractType = formMethods.watch("fundingRequestType");
  const status = formMethods.watch("fundingRequestStatus");
  const displayBanner = useMemo(
    () =>
      (contractType &&
        [FR_CONTRACT_TYPE[1].value, FR_CONTRACT_TYPE[2].value].includes(contractType?.value)) ||
      ([FR_STATUS[7].value, FR_STATUS[6].value].includes(status?.value ?? "") &&
        !isDesktop &&
        isNonFactorRefactorEnabled),
    [contractType, status]
  );

  return (
    <>
      <LoadingOverlay isVisible={isSubmitting} />
      {isLockedForReview && (
        <div className={clsx("mb-3", isDesktop ? "px-1 desktop:px-0" : "mx-2")}>
          <Banner
            variant="warning"
            message="This funding request is under HaulPay Review and is locked."
          />
        </div>
      )}
      {isDesktop ? (
        // Desktop layout - 3 columns
        <div
          className="
            grid grid-cols-1 gap-2 h-full px-1 pb-20
            desktop:px-0 desktop:pb-0
            desktop:[grid-template-columns:clamp(220px,22vw,320px)_minmax(0,1fr)_clamp(240px,24vw,360px)]
          ">
          {/* Left summary column (fluid width on desktop) */}
          <aside className="flex flex-col gap-3 order-3 desktop:order-1 mt-4 desktop:mt-0">
            {displayBanner && isCarrier && (
              <div className="flex mb-4">
                <Banner
                  variant="warning"
                  message="Once the required documents (a signed Bill of Lading showing delivery or a TONU) are uploaded, this funding request will be invoiced at 8am, 12pm, and 5pm PST, provided your NOA is active with the debtor."
                  dismissible
                />
              </div>
            )}
            <LeftSummary />
          </aside>

          {/* Middle — min-w-0 so it doesn't get squished by wide children */}
          <main className="order-1 desktop:order-2 min-w-0">
            <MiddleSections />
          </main>

          {/* Right (fluid width on desktop) */}
          <aside className="flex flex-col gap-3 order-2 desktop:order-3 mt-4 desktop:mt-0 desktop:sticky desktop:top-4 desktop:self-start desktop:max-h-[calc(100vh-2rem)] desktop:overflow-y-auto">
            <RightActions />

            {/* HP-8809: the rail itself scrolls. RightActions cannot shrink below its content, so
                on a short viewport it consumed the whole rail and left the uploads pane 0px tall
                with nothing above it able to scroll — the upload control was unreachable. */}
            <div className="hidden desktop:block">
              <div className="border-t border-card pt-4">
                <UploadDocumentsCard />
                <div
                  className="mt-4"
                  ref={(el) => {
                    anchorRef.current = el;
                    setActiveAnchor(el);
                  }}
                />
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <div className="relative mx-2 h-full">
          <Swiper
            modules={[Pagination]}
            onSwiper={setSwiperInstance}
            touchStartPreventDefault={false}
            threshold={10}
            pagination={{
              clickable: true,
              type: "bullets",
              dynamicBullets: false
            }}
            className={clsx(
              "swiper-funding-request relative h-full [&_.swiper-pagination]:!absolute [&_.swiper-pagination]:!bottom-20 [&_.swiper-pagination]:!left-1/2 [&_.swiper-pagination]:!transform [&_.swiper-pagination]:!-translate-x-1/2 [&_.swiper-pagination]:!z-20 [&_.swiper-pagination-bullet]:!w-3 [&_.swiper-pagination-bullet]:!h-3 [&_.swiper-pagination-bullet]:!bg-gray-300 [&_.swiper-pagination-bullet]:!opacity-100 [&_.swiper-pagination-bullet]:!rounded-full [&_.swiper-pagination-bullet]:!mx-1 [&_.swiper-pagination-bullet-active]:!bg-blue-500 [&_.swiper-pagination-bullet-active]:!scale-125",
              isKeyboardVisible && "[&_.swiper-pagination]:!hidden"
            )}>
            <SwiperSlide>
              <div className="h-full overflow-y-auto flex flex-col">
                <div className="flex-1 pb-12">
                  {displayBanner && isCarrier && (
                    <div className="flex mb-4 mx-4">
                      <Banner
                        variant="warning"
                        message="Once the required documents (a signed Bill of Lading showing delivery or a TONU) are uploaded, this funding request will be invoiced at 8am, 12pm, and 5pm PST, provided your NOA is active with the debtor."
                        dismissible
                      />
                    </div>
                  )}
                  <LeftSummary />
                </div>

                {/* Update Button - Sticky at Bottom */}
                <div
                  className={clsx(
                    "sticky bottom-0 p-3 pb-5 mt-4 bg-background z-10",
                    isKeyboardVisible && "hidden"
                  )}>
                  {showCarrierSubmitActions ? (
                    <CarrierActions tooltipPosition="up" tooltipContainerClassName="w-full" />
                  ) : isBroker ? (
                    <Button
                      variant="contained"
                      className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white"
                      onClick={handleSubmitClick}
                      disabled={isButtonDisabled()}>
                      <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                      {updateBtnLabel()}
                    </Button>
                  ) : (
                    <Tooltip
                      message={`Upload a signed BOL to mark this request as ${isAssignedLoadsPage ? "Delivered" : "completed"}`}
                      position="up"
                      tooltipContainerClassName={isAssignedLoadsPage ? "flex flex-col" : "relative"}
                      disabled={hasBOLDocument || !isAssignedLoadsMode}>
                      <Button
                        type="button"
                        className={clsx(
                          "w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white",
                          isCompletionStateActive ? "!bg-green-500 !text-white" : ""
                        )}
                        onClick={handleSubmitClick}
                        disabled={isButtonDisabled()}>
                        <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                        {updateBtnLabel()}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="h-full overflow-y-auto flex flex-col">
                <div className="flex-1 pb-12">
                  <MiddleSections />
                </div>

                {/* Update Button - Sticky at Bottom */}
                <div
                  className={clsx(
                    "sticky bottom-0 p-3 pb-5 mt-4 bg-background z-10",
                    isKeyboardVisible && "hidden"
                  )}>
                  {showCarrierSubmitActions ? (
                    <CarrierActions tooltipPosition="up" tooltipContainerClassName="w-full" />
                  ) : isBroker ? (
                    <Button
                      variant="contained"
                      className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white"
                      onClick={handleSubmitClick}
                      disabled={isButtonDisabled()}>
                      <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                      {updateBtnLabel()}
                    </Button>
                  ) : (
                    <Tooltip
                      message={`Upload a signed BOL to mark this request as ${isAssignedLoadsPage ? "Delivered" : "completed"}`}
                      position="up"
                      tooltipContainerClassName="flex-1"
                      disabled={hasBOLDocument || !isAssignedLoadsMode}>
                      <Button
                        type="button"
                        className={clsx(
                          "w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white",
                          isCompletionStateActive ? "!bg-green-500 !text-white" : ""
                        )}
                        onClick={handleSubmitClick}
                        disabled={isButtonDisabled()}>
                        <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                        {updateBtnLabel()}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="h-full overflow-y-auto flex flex-col">
                <div className="flex-1 pb-12">
                  <RightActions />
                  <div className="border-t border-card pt-4 mt-4">
                    <UploadDocumentsCard />
                    <div
                      className="mt-3"
                      ref={(el) => {
                        anchorRef.current = el;
                        setActiveAnchor(el);
                      }}
                    />
                  </div>
                </div>

                {/* Update Button - Sticky at Bottom */}
                <div
                  className={clsx(
                    "sticky bottom-0 p-3 pb-5 mt-4 bg-background z-10",
                    isKeyboardVisible && "hidden"
                  )}>
                  {showCarrierSubmitActions ? (
                    <CarrierActions tooltipPosition="up" tooltipContainerClassName="w-full" />
                  ) : isBroker ? (
                    <Button
                      variant="contained"
                      className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white"
                      onClick={handleSubmitClick}
                      disabled={isButtonDisabled()}>
                      <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                      {updateBtnLabel()}
                    </Button>
                  ) : (
                    <Tooltip
                      message={`Upload a signed BOL to mark this request as ${isAssignedLoadsPage ? "Delivered" : "completed"}`}
                      position="up"
                      tooltipContainerClassName="flex-1"
                      disabled={hasBOLDocument || !isAssignedLoadsMode}>
                      <Button
                        type="button"
                        className={clsx(
                          "w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white",
                          isCompletionStateActive ? "!bg-green-500 !text-white" : ""
                        )}
                        onClick={handleSubmitClick}
                        disabled={isButtonDisabled()}>
                        <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                        {updateBtnLabel()}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      )}

      {/* Render AttachmentsListCard INTO the visible anchor (desktop only) */}
      {activeAnchor && ReactDOM.createPortal(<AttachmentsListCard />, activeAnchor)}

      {/* Calculating snackbar */}
      {isFetchingCalculation && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white px-6 py-3 rounded-xl shadow-[0_0_10px_rgba(59,130,246,0.3)] border border-blue-500 flex items-center gap-3 text-base">
          <Icon path={mdiLoading} size={1} spin={1} className="text-blue-400" />
          <span className="text-lg">
            Calculating
            <span className="inline-flex w-12 gap-1 ml-1 text-xl">
              <span className="animate-[bounce_1s_infinite_0ms]">.</span>
              <span className="animate-[bounce_1s_infinite_200ms]">.</span>
              <span className="animate-[bounce_1s_infinite_400ms]">.</span>
            </span>
          </span>
        </div>
      )}
    </>
  );
}

export default function ManageFundingRequestV2({ isAssignedLoad }: { isAssignedLoad?: boolean }) {
  return (
    <FundingRequestFormProvider isAssignedLoad={isAssignedLoad}>
      <FundingRequestUpdateActionProvider>
        <FundingRequestLayout />
      </FundingRequestUpdateActionProvider>
    </FundingRequestFormProvider>
  );
}
